import { IPortfolio } from '@/interfaces/portfolioOptimization.interface'
import axiosInstance from '@/utils/interceptor'
import dayjs, { ManipulateType } from 'dayjs'

// Date-range options for the What-If portfolio list dropdown. 'all' omits the
// beginDate/endDate params entirely (no date restriction).
export type WhatIfPortfolioRangeKey = '1w' | '2w' | '1m' | '6m' | '1y' | 'all'

export const WHAT_IF_PORTFOLIO_RANGE_OPTIONS: Array<{
  key: WhatIfPortfolioRangeKey
  label: string
}> = [
  { key: '1w', label: '1 Week' },
  { key: '2w', label: '2 Weeks' },
  { key: '1m', label: '1 Month' },
  { key: '6m', label: '6 Months' },
  { key: '1y', label: '1 Year' },
  { key: 'all', label: 'All' },
]

export const DEFAULT_WHAT_IF_PORTFOLIO_RANGE: WhatIfPortfolioRangeKey = '2w'

const RANGE_LOOKBACK: Record<Exclude<WhatIfPortfolioRangeKey, 'all'>, [number, ManipulateType]> = {
  '1w': [1, 'week'],
  '2w': [2, 'week'],
  '1m': [1, 'month'],
  '6m': [6, 'month'],
  '1y': [1, 'year'],
}

// Cache for What-If portfolio lists, keyed by range, to prevent duplicate API calls
const whatIfPortfolioCache: Partial<Record<WhatIfPortfolioRangeKey, IPortfolio[]>> = {}
const whatIfPortfolioPromise: Partial<Record<WhatIfPortfolioRangeKey, Promise<IPortfolio[]>>> = {}

const fetchWhatIfPortfolios = (params?: { beginDate: string; endDate: string }): Promise<IPortfolio[]> => {
  return axiosInstance.get('portfolio', { params }).then((response) => {
    const allPortfolios = response.data as IPortfolio[]
    // Filter out swap portfolios (keep only type != 'swap')
    return allPortfolios.filter((p) => !p.type || p.type.toLowerCase() !== 'swap')
  })
}

/**
 * Get the What-If portfolio list for the given date range, with per-range caching to
 * prevent duplicate calls.
 * @param range 'all' returns every portfolio with no date restriction; any other key
 * restricts to portfolios dated within that lookback window of today.
 */
export const getWhatIfPortfolios = async (
  range: WhatIfPortfolioRangeKey = DEFAULT_WHAT_IF_PORTFOLIO_RANGE,
): Promise<IPortfolio[]> => {
  const cached = whatIfPortfolioCache[range]
  if (cached) {
    return cached
  }

  const inFlight = whatIfPortfolioPromise[range]
  if (inFlight) {
    return inFlight
  }

  const params =
    range === 'all'
      ? undefined
      : {
          endDate: dayjs().format('YYYYMMDD'),
          beginDate: dayjs().subtract(...RANGE_LOOKBACK[range]).format('YYYYMMDD'),
        }

  const promise = fetchWhatIfPortfolios(params)
    .then((portfolios) => {
      whatIfPortfolioCache[range] = portfolios
      delete whatIfPortfolioPromise[range]
      return portfolios
    })
    .catch((error) => {
      delete whatIfPortfolioPromise[range]
      throw error
    })

  whatIfPortfolioPromise[range] = promise
  return promise
}

/**
 * Clear all cached What-If portfolio lists (every range) - useful for refreshing data
 */
export const clearWhatIfPortfolioCache = (): void => {
  for (const key of Object.keys(whatIfPortfolioCache) as WhatIfPortfolioRangeKey[]) {
    delete whatIfPortfolioCache[key]
  }
  for (const key of Object.keys(whatIfPortfolioPromise) as WhatIfPortfolioRangeKey[]) {
    delete whatIfPortfolioPromise[key]
  }
}

// Test-only hook so Cypress can invalidate the module-level cache mid-test, since it
// otherwise only clears on a full page reload (see WhatIfReportsPage.cy.ts).
if ((window as any)?.Cypress) {
  (window as any).__clearWhatIfPortfolioCache = clearWhatIfPortfolioCache
}

// Batch API - commented out for future use
// export const getSecurityDetailsBatch = async (securityIds: string[], date: string): Promise<any> => {
//   const response = await axiosInstance.post(`whatif/api/v1/whatif/getsecurities`, {
//     date: parseInt(date),
//     secids: securityIds
//   })
//   return response.data
// }

// Individual security API - currently active implementation
export const getSecurityDetails = async (securityId: string, date: string): Promise<any> => {
  const response = await axiosInstance.get(`whatif/api/v1/whatif/getsecurity`, {
    params: {
      date: parseInt(date),
      securityId: securityId
    }
  })
  return response.data
}

// Calculate market value API (for transaction report)
export interface CalcMkvalParams {
  date: string | number
  units: number
  mval: number
  currencyCode: number
  baseCurrencyCode: number
}

export interface CalcMkvalResponse {
  mkval: number
}

export const calcmkval = async (
  params: CalcMkvalParams,
): Promise<CalcMkvalResponse> => {
  const response = await axiosInstance.get(
    '/whatif/api/v1/whatif/calcmkval',
    { params },
  )
  return response.data
}

/**
 * Get securities for a specific portfolio in What-If analysis
 * @param portfolioId 
 * @param date 
 * @returns Promise with portfolio securities
 */
export const getPortfolioSecurities = async (portfolioId: string, date?: string): Promise<any> => {
  const response = await axiosInstance.get(`whatif/portfolio/${portfolioId}/securities`, {
    params: date ? { date } : {}
  })
  return response.data
}

// Target trades result API
export interface TargetTradesResultParams {
  trades: number
  date: string
  units: number
  mval: number
  fmval: number
  durVal: number
  currencyCode: number
  baseCurrencyCode: number
  portMkval: number
}

export interface TargetTradesResultResponse {
  durContrib: number
  mkval: number
  pmkval: number
  trades: number
}

export const getTargetTradesResult = async (
  params: TargetTradesResultParams
): Promise<TargetTradesResultResponse> => {
  const response = await axiosInstance.get('/whatif/api/v1/whatif/gettargettradesresult', {
    params
  })
  return response.data
}

// Target duration contribution result API (for what-if contribution duration)
export interface TargetDurContResultParams {
  durCont: number
  date: string
  units: number
  mval: number
  fmval: number
  durVal: number
  currencyCode: number
  baseCurrencyCode: number
  portMkval: number
}

export interface TargetDurContResultResponse {
  durContrib: number
  mkval: number
  pmkval: number
  trades: number
}

export const getTargetDurContResult = async (
  params: TargetDurContResultParams,
): Promise<TargetDurContResultResponse> => {
  const response = await axiosInstance.get(
    '/whatif/api/v1/whatif/gettargetdurcontresult',
    {
      params,
    },
  )
  return response.data 
}

// Target percent of portfolio result API (for what-if %PORT)
export interface TargetPmkvalResultParams {
  pmkval: number
  date: string
  units: number
  mval: number
  fmval: number
  durVal: number
  currencyCode: number
  baseCurrencyCode: number
  portMkval: number
}

export interface TargetPmkvalResultResponse {
  durContrib: number
  mkval: number
  pmkval: number
  trades: number
}

export const getTargetPmkvalResult = async (
  params: TargetPmkvalResultParams,
): Promise<TargetPmkvalResultResponse> => {
  const response = await axiosInstance.get(
    '/whatif/api/v1/whatif/gettargetpmkvalresult',
    {
      params,
    },
  )
  return response.data
}

// Target market value result API (for cash row updates)
export interface TargetMkvalResultParams {
  mkval: number
  date: string
  units: number
  mval: number
  fmval: number
  durVal: number
  currencyCode: number
  baseCurrencyCode: number
  portMkval: number
}

// Response shape is the same as TargetTradesResultResponse
export type TargetMkvalResultResponse = TargetTradesResultResponse

export const getTargetMkvalResult = async (
  params: TargetMkvalResultParams
): Promise<TargetMkvalResultResponse> => {
  const response = await axiosInstance.get('/whatif/api/v1/whatif/gettargetmkvalresult', {
    params
  })
  return response.data
}

// Interface for summary API response
export interface SummaryApiResponse {
  uuid: string
  status: string
  summaryData: Array<{
    mkval_0: number
    mkval_1: number
    mkval_diff: number
    mkval_b: number
    edur_0: number
    edur_1: number
    edur_diff: number
    edur_b: number | null
    pdur_0: number
    pdur_1: number
    pdur_diff: number
    pdur_b: number | null
    ed2_0: number
    ed2_1: number
    ed2_diff: number
    ed2_b: number | null
    ed3_0: number
    ed3_1: number
    ed3_diff: number
    ed3_b: number | null
    cvx_0: number
    cvx_1: number
    cvx_diff: number
    cvx_b: number | null
    yte_0: number
    yte_1: number
    yte_diff: number
    yte_b: number | null
    qual_0: string
    qual_1: string
    qual_b: string
    coup_0: number
    coup_1: number
    coup_diff: number
    coup_b: number | null
    yrtomatu_0: number
    yrtomatu_1: number
    yrtomatu_diff: number
    yrtomatu_b: number | null
    avglife_0: number
    avglife_1: number
    avglife_diff: number
    avglife_b: number | null
    totrisk_0: number
    totrisk_1: number
    totrisk_diff: number
    totrisk_b: number
    track_0: number
    track_1: number
    track_diff: number
    track_b: number
    var_0: number
    var_1: number
    var_diff: number
    var_b: number
  }>
  riskData: RiskDataItem[]
}

/**
 * Get summary data for What-If analysis comparison
 * @param date - Date in YYYYMMDD format
 * @param portfolioName - Original portfolio name
 * @param whatIfPortfolioName - What-if portfolio name (optional)
 * @returns Promise with summary comparison data and risk data
 */
export const getWhatIfSummary = async (date: string, portfolioName: string, whatIfPortfolioName?: string): Promise<SummaryApiResponse> => {
  const response = await axiosInstance.get('/whatif/api/v1/whatif/getsummary', {
    params: {
      date,
      portfolioName
    }
  })
  return response.data
}

// Risk Data API interfaces
export interface RiskDataItem {
  is_before: boolean
  total_risk: number
  yield_risk: number
  sector_risk: number
  quality_risk: number
  other_risk: number
  currency_risk: number
  total_risk_b: number
  yield_risk_b: number
  sector_risk_b: number
  quality_risk_b: number
  other_risk_b: number
  currency_risk_b: number
}


// Sector Composition API interfaces
export interface SectorCompositionItem {
  sector: string
  composition_0: number
  composition_1: number
  composition_b: number
}

export interface SectorCompositionApiResponse {
  sectorComposition: SectorCompositionItem[]
  uuid: string
  status: string
}

export const getWhatIfSectorComposition = async (date: string, portfolioName: string, whatIfPortfolioName?: string): Promise<SectorCompositionApiResponse> => {
  const response = await axiosInstance.get('/whatif/api/v1/whatif/getsectorcomposition', {
    params: {
      date,
      portfolioName
    }
  })
  return response.data
}

// Save What-If Portfolio API interfaces
export interface WhatIfHolding {
  axsid: number
  hld: number
}

export interface SaveWhatIfPortfolioRequest {
  replaceIfExists: boolean
  portDate: number
  portName: string
  whatIfPortName: string
  holdings: WhatIfHolding[]
}

export interface SaveWhatIfPortfolioResponse {
  success: boolean
  message?: string
  uuid?: string
}

export const saveWhatIfPortfolio = async (request: SaveWhatIfPortfolioRequest): Promise<SaveWhatIfPortfolioResponse> => {
  const response = await axiosInstance.post('/whatif/api/v1/whatif/save', request)
  return response.data
}

// Interface for transformed summary data for the table
export interface SummaryTableData {
  metric: string
  dbField: string
  before: string
  after: string
  change: string
  benchmark: string
}

// Interface for transaction report data
export interface TrasReportItem {
  secid: string
  price: number
  qual: string
  holdings_current: number
  holdings_whatif: number
  mkval: number
  description: string
  coup: number
}

/**
 * Transform API response to table format
 * Field mapping:
 * - _0 fields = Before (original portfolio)
 * - _1 fields = After (what-if portfolio)  
 * - _diff fields = Change (difference)
 * - _b fields = Benchmark
 * @param apiResponse - Raw API response
 * @returns Array of formatted table data
 */
export const transformSummaryData = (apiResponse: SummaryApiResponse): SummaryTableData[] => {
  if (!apiResponse.summaryData || apiResponse.summaryData.length === 0) {
    return []
  }

  const data = apiResponse.summaryData[0] 

  // Helper function to format numbers
  const formatNumber = (value: number | null, decimals: number = 2): string => {
    if (value === null || value === undefined) return '0.00'
    return value.toLocaleString('en-US', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    })
  }


  return [
    {
      metric: 'Market Value ($ Millions)',
      dbField: 'Mkval',
      before: formatNumber(data.mkval_0),      
      after: formatNumber(data.mkval_1),       
      change: formatNumber(data.mkval_diff),      
      benchmark: formatNumber(data.mkval_b)    
    },
    {
      metric: 'Level Effective Duration',
      dbField: 'EDur',
      before: formatNumber(data.edur_0, 2),
      after: formatNumber(data.edur_1, 2),
      change: formatNumber(data.edur_diff, 2),
      benchmark: formatNumber(data.edur_b, 2)
    },
    {
      metric: 'Level Par Effective Duration',
      dbField: 'PDur',
      before: formatNumber(data.pdur_0, 2),
      after: formatNumber(data.pdur_1, 2),
      change: formatNumber(data.pdur_diff, 2),
      benchmark: formatNumber(data.pdur_b, 2)
    },
    {
      metric: 'Slope Effective Duration',
      dbField: 'ED2',
      before: formatNumber(data.ed2_0, 2),
      after: formatNumber(data.ed2_1, 2),
      change: formatNumber(data.ed2_diff, 2),
      benchmark: formatNumber(data.ed2_b, 2)
    },
    {
      metric: 'Curvature Effective Duration',
      dbField: 'ED3',
      before: formatNumber(data.ed3_0, 2),
      after: formatNumber(data.ed3_1, 2),
      change: formatNumber(data.ed3_diff, 2),
      benchmark: formatNumber(data.ed3_b, 2)
    },
    {
      metric: 'Convexity',
      dbField: 'Cvx',
      before: formatNumber(data.cvx_0, 2),
      after: formatNumber(data.cvx_1, 2),
      change: formatNumber(data.cvx_diff, 2),
      benchmark: formatNumber(data.cvx_b, 2)
    },
    {
      metric: 'Yield to Effective',
      dbField: 'YTE',
      before: formatNumber(data.yte_0, 2),
      after: formatNumber(data.yte_1, 2),
      change: formatNumber(data.yte_diff, 2),
      benchmark: formatNumber(data.yte_b, 2)
    },
    {
      metric: 'Quality',
      dbField: 'Qual',
      before: data.qual_0 || 'N/A',
      after: data.qual_1 || 'N/A',
      change: data.qual_0 === data.qual_1 ? 'No Change' : 'Changed',
      benchmark: data.qual_b || 'N/A'
    },
    {
      metric: 'Coupon',
      dbField: 'Coupon',
      before: formatNumber(data.coup_0, 2),
      after: formatNumber(data.coup_1, 2),
      change: formatNumber(data.coup_diff, 2),
      benchmark: formatNumber(data.coup_b, 2)
    },
    {
      metric: 'Years to Maturity',
      dbField: 'Yrs to Mat',
      before: formatNumber(data.yrtomatu_0, 2),
      after: formatNumber(data.yrtomatu_1, 2),
      change: formatNumber(data.yrtomatu_diff, 2),
      benchmark: formatNumber(data.yrtomatu_b, 2)
    },
    {
      metric: 'Average Life',
      dbField: 'AvgLif',
      before: formatNumber(data.avglife_0, 2),
      after: formatNumber(data.avglife_1, 2),
      change: formatNumber(data.avglife_diff, 2),
      benchmark: formatNumber(data.avglife_b, 2)
    },
    {
      metric: 'Risk',
      dbField: 'Risk',
      before: formatNumber(data.totrisk_0, 2),
      after: formatNumber(data.totrisk_1, 2),
      change: formatNumber(data.totrisk_diff, 2),
      benchmark: formatNumber(data.totrisk_b, 2)
    },
    {
      metric: 'Tracking Error',
      dbField: 'T/E',
      before: formatNumber(data.track_0, 2),
      after: formatNumber(data.track_1, 2),
      change: formatNumber(data.track_diff, 2),
      benchmark: formatNumber(data.track_b, 2)
    },
    {
      metric: '1 Day 95% Value at Risk (000)',
      dbField: 'Var95D',
      before: formatNumber(data.var_0, 2),
      after: formatNumber(data.var_1, 2),
      change: formatNumber(data.var_diff, 2),
      benchmark: formatNumber(data.var_b, 2)
    }
  ]
}
